import { useCallback, useEffect, useState } from "react";
import { ApiError, apiDelete, apiGet, apiPost, apiPut } from "../lib/api";
import type { PostTemplate } from "../lib/types";
import { useAuth } from "../auth/AuthContext";
import { useScope } from "../scope/ScopeContext";

type Draft = Omit<PostTemplate, "id" | "createdAt" | "updatedAt" | "lastPublishedAt" | "dealerId"> & {
  id?: string;
  dealerId?: string;
};

const EMPTY_DRAFT: Draft = {
  name: "",
  category: "inventory",
  copyTemplate: "",
  mediaStrategy: { preferred: ["inventory", "library"], fallback: "library" },
  cadenceRrule: null,
  targetPlatforms: ["gbp", "facebook"],
  complianceRequired: false,
  variables: [],
  active: true,
};

export function TemplatesPage() {
  const { user } = useAuth();
  const { focusedDealerId, dealers } = useScope();
  const [templates, setTemplates] = useState<PostTemplate[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // For "+ New template": dealer comes from focus, or the user's own dealer if dealer-scoped,
  // or — for super_admin / group_admin with no focus — the first visible dealer as a sensible default.
  const newTemplateDealerId =
    focusedDealerId ?? user?.dealerId ?? (dealers[0]?.id ?? undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      const path = params.toString() ? `/api/templates?${params.toString()}` : "/api/templates";
      const tplRes = await apiGet<{ templates: PostTemplate[] }>(path);
      setTemplates(tplRes.templates);
    } catch (err) {
      console.error("templates load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const startNew = () => {
    setEditing({ ...EMPTY_DRAFT, dealerId: newTemplateDealerId });
  };

  const startEdit = (tpl: PostTemplate) => {
    setEditing({
      id: tpl.id,
      dealerId: tpl.dealerId,
      name: tpl.name,
      category: tpl.category,
      copyTemplate: tpl.copyTemplate,
      mediaStrategy: tpl.mediaStrategy,
      cadenceRrule: tpl.cadenceRrule,
      targetPlatforms: tpl.targetPlatforms,
      complianceRequired: tpl.complianceRequired,
      variables: tpl.variables,
      active: tpl.active,
    });
  };

  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.copyTemplate.trim()) {
      alert("Name and copy template are required.");
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        await apiPut(`/api/templates/${editing.id}`, editing);
      } else {
        await apiPost("/api/templates", editing);
      }
      await load();
      setEditing(null);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      alert(`Save failed: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (tpl: PostTemplate) => {
    if (!window.confirm(`Deactivate "${tpl.name}"? (existing scheduled posts keep their history.)`)) return;
    setBusy(true);
    try {
      await apiDelete(`/api/templates/${tpl.id}`);
      await load();
    } catch (err) {
      alert(`Deactivate failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Templates</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pre-approved post bodies. AI fills variables at compose time; sales/financing templates hard-gate through compliance.
          </p>
        </div>
        <button
          onClick={startNew}
          className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          + New template
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && templates.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            Your post library is empty. Click <span className="font-semibold">+ New template</span> to add one.
          </div>
        )}
        {templates.map((tpl) => (
          <article
            key={tpl.id}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">{tpl.name}</h3>
                  <CategoryBadge category={tpl.category} />
                  {tpl.complianceRequired && (
                    <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-700 ring-1 ring-inset ring-rose-200">
                      compliance gate
                    </span>
                  )}
                  {!tpl.active && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      inactive
                    </span>
                  )}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-600">{tpl.copyTemplate}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <span>Platforms: {tpl.targetPlatforms.join(" + ")}</span>
                  {tpl.cadenceRrule && <span>Cadence: <code className="text-[10px]">{tpl.cadenceRrule}</code></span>}
                  <span>{tpl.variables.length} variables</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  onClick={() => startEdit(tpl)}
                  className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                >
                  Edit
                </button>
                {tpl.active && (
                  <button
                    onClick={() => void deactivate(tpl)}
                    className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-rose-700"
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {editing && (
        <TemplateEditor
          draft={editing}
          onChange={setEditing}
          onCancel={cancel}
          onSave={() => void save()}
          busy={busy}
        />
      )}
    </div>
  );
}

function CategoryBadge({ category }: { category: PostTemplate["category"] }) {
  const styles: Record<PostTemplate["category"], string> = {
    inventory: "bg-sky-50 text-sky-700 ring-sky-200",
    service: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    sales: "bg-amber-50 text-amber-700 ring-amber-200",
    brand: "bg-violet-50 text-violet-700 ring-violet-200",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[category]}`}
    >
      {category}
    </span>
  );
}

interface EditorProps {
  draft: Draft;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  busy: boolean;
}

function TemplateEditor({ draft, onChange, onCancel, onSave, busy }: EditorProps) {
  const togglePlatform = (p: "gbp" | "facebook") => {
    const next = draft.targetPlatforms.includes(p)
      ? draft.targetPlatforms.filter((x) => x !== p)
      : [...draft.targetPlatforms, p];
    onChange({ ...draft, targetPlatforms: next });
  };

  return (
    <div className="mt-8 rounded-2xl border border-slate-300 bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">
          {draft.id ? "Edit template" : "New template"}
        </h2>
        <button
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-900"
          type="button"
        >
          ✕ Close
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Name</span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Category</span>
          <select
            value={draft.category}
            onChange={(e) =>
              onChange({
                ...draft,
                category: e.target.value as Draft["category"],
                complianceRequired:
                  e.target.value === "sales" ? true : draft.complianceRequired,
              })
            }
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
          >
            <option value="inventory">Inventory</option>
            <option value="service">Service</option>
            <option value="sales">Sales / Financing</option>
            <option value="brand">Brand</option>
          </select>
        </label>
      </div>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-700">Copy template</span>
        <textarea
          rows={5}
          value={draft.copyTemplate}
          onChange={(e) => onChange({ ...draft, copyTemplate: e.target.value })}
          placeholder={"e.g. \"This week at {{dealer.name}}: {{vehicle.year}} {{vehicle.make}} {{vehicle.model}} {{vehicle.trim}} — {{vehicle.sellingPrice}}.\""}
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
        />
        <span className="mt-1 block text-[11px] text-slate-500">
          Use <code>{`{{vehicle.*}}`}</code>, <code>{`{{incentive.*}}`}</code>, <code>{`{{dealer.*}}`}</code>. AI fills these at compose time from Savvy API + incentive-matcher.
        </span>
      </label>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <fieldset>
          <legend className="text-xs font-medium text-slate-700">Target platforms</legend>
          <div className="mt-2 flex gap-2">
            {(["gbp", "facebook"] as const).map((p) => (
              <label
                key={p}
                className={`cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium ${
                  draft.targetPlatforms.includes(p)
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={draft.targetPlatforms.includes(p)}
                  onChange={() => togglePlatform(p)}
                  className="sr-only"
                />
                {p === "gbp" ? "Google Business Profile" : "Facebook Page"}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">
            Cadence (RRULE — leave blank for manual)
          </span>
          <input
            type="text"
            value={draft.cadenceRrule ?? ""}
            onChange={(e) => onChange({ ...draft, cadenceRrule: e.target.value || null })}
            placeholder="e.g. FREQ=WEEKLY;BYDAY=TU"
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs shadow-inner"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm">
        <label className="flex items-center gap-2 text-slate-700">
          <input
            type="checkbox"
            checked={draft.complianceRequired}
            onChange={(e) => onChange({ ...draft, complianceRequired: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span>Compliance gate required (sales / financing)</span>
        </label>
        <label className="flex items-center gap-2 text-slate-700">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => onChange({ ...draft, active: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span>Active</span>
        </label>
      </div>

      <VariableEditor
        variables={draft.variables}
        onChange={(vars) => onChange({ ...draft, variables: vars })}
      />

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          disabled={busy}
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          type="button"
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          disabled={busy}
        >
          {busy ? "Saving…" : draft.id ? "Save changes" : "Create template"}
        </button>
      </div>
    </div>
  );
}

interface VariableEditorProps {
  variables: Draft["variables"];
  onChange: (vars: Draft["variables"]) => void;
}

function VariableEditor({ variables, onChange }: VariableEditorProps) {
  const update = (idx: number, patch: Partial<Draft["variables"][number]>) => {
    onChange(variables.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const remove = (idx: number) => onChange(variables.filter((_, i) => i !== idx));
  const add = () => onChange([...variables, { key: "", source: "vehicle", required: true }]);

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">Variables</h3>
        <button
          onClick={add}
          type="button"
          className="text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          + Add variable
        </button>
      </div>
      {variables.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          None declared — AI will fill any <code>{`{{...}}`}</code> reference present in the copy template.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {variables.map((v, i) => (
            <li key={i} className="grid grid-cols-6 items-center gap-2 sm:grid-cols-12">
              <input
                type="text"
                placeholder="key"
                value={v.key}
                onChange={(e) => update(i, { key: e.target.value })}
                className="col-span-6 rounded-md border border-slate-300 px-2 py-1 text-xs font-mono sm:col-span-5"
              />
              <select
                value={v.source}
                onChange={(e) => update(i, { source: e.target.value as Draft["variables"][number]["source"] })}
                className="col-span-3 rounded-md border border-slate-300 px-2 py-1 text-xs sm:col-span-4"
              >
                <option value="vehicle">vehicle</option>
                <option value="incentive">incentive</option>
                <option value="dealer">dealer</option>
                <option value="static">static</option>
              </select>
              <label className="col-span-2 flex items-center gap-1 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={v.required}
                  onChange={(e) => update(i, { required: e.target.checked })}
                />
                required
              </label>
              <button
                onClick={() => remove(i)}
                type="button"
                className="col-span-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-rose-700"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
